import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ADMIN_YOUTUBE_EMAILS } from "../../../utils/constants";
import {
  FaMagnifyingGlass,
  FaUpload,
  FaVideo,
  FaArrowLeft,
  FaPlay,
  FaAngleDown,
  FaTrash
} from "react-icons/fa6";
import { FiFilter, FiSend } from "react-icons/fi";
import { IoVideocamOutline } from "react-icons/io5";
import { LuPen } from "react-icons/lu";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Card, CardContent } from "../../../components/ui/card";
import Button from "../../../components/button-v1/index";
import Badge from "../../../components/inventory/modules/Badge/Badge";
import CustomInput from "../../../components/ui/input";
import { Dropdown } from "../../../components/ui/dropdown";
import UploadModal from "./upload-video/uploadVideoModal";
import SendModal from "./send-video/sendVideoModal";
import AddCategoryModal from "./add-category/addCategoryModal";
import EditVideoModal from "./edit-video/editVideoModal";
import PlayVideoModal from "./play-video/playVideoModal";
import Spinner from "../../../components/spinner";
import styles from "./instructional-video.module.css";
import { 
  useAdminDashboardData,
  useUpdateVideo,
  useDeleteVideo,
  useCreateCategory,
  useSyncData,
  useGetAuthUrl
} from "../../../utils/youtubeApiQueries";
import toast from "react-hot-toast";

const parseDuration = (pt) => {
  if (!pt) return "0:00";
  if (typeof pt === "string" && pt.includes(":")) return pt;
  const hours = pt.match(/(\d+)H/);
  const minutes = pt.match(/(\d+)M/);
  const seconds = pt.match(/(\d+)S/);
  const h = hours ? parseInt(hours[1]) : 0;
  const m = minutes ? parseInt(minutes[1]) : 0;
  const s = seconds ? parseInt(seconds[1]) : 0;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Function to get admin emails from .env file only
// const getAdminYouTubeEmails = () => {
//   const envEmails = process.env.REACT_APP_ADMIN_YOUTUBE_EMAILS;
//   if (!envEmails) {
//     throw new Error('REACT_APP_ADMIN_YOUTUBE_EMAILS environment variable is required');
//   }
//   return envEmails.split(',').map(e => e.trim().toLowerCase());
// };

// const ADMIN_YOUTUBE_EMAILS = getAdminYouTubeEmails();

// const ADMIN_YOUTUBE_EMAILS = [\
    // "siam.cheema@neurooceans.ai, 
    // umair.saeed@neurooceans.ai, 
    // m.fahid@tocahealth.com, 
    // D.gaur@tocahealth.com, 
    // d.gandhi@tocahealth.com"
// ].map((e) => e.toLowerCase());

const getUserEmailFromLocalStorage = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const user = JSON.parse(raw);
    return (
      user?.email ||
      user?.account?.email ||
      user?.auth_user?.email ||
      null
    );
  } catch (e) {
    console.error("Failed to parse user from localStorage:", e);
    return null;
  }
};

const InstructionalVideoDashboard = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  // const [selectedVideos, setSelectedVideos] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // const [showSendModal, setShowSendModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [showPlayModal, setShowPlayModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const isMounted = useRef(true);

  const userEmail = getUserEmailFromLocalStorage();
  const isFrontendAdmin = !!userEmail && ADMIN_YOUTUBE_EMAILS.includes(userEmail.toLowerCase());
  const canManage = isFrontendAdmin;

  const { videos, categories: categoriesData, authStatus, isLoading, error, refetch } = useAdminDashboardData(canManage);
  const updateVideoMutation = useUpdateVideo();
  const deleteVideoMutation = useDeleteVideo();
  const createCategoryMutation = useCreateCategory();
  const syncDataMutation = useSyncData();
  const getAuthUrlQuery = useGetAuthUrl();

  const isSignedIn = !!authStatus?.has_auth;

  const categories = [
    { value: "all", label: "All Categories" },
    ...categoriesData.map(cat => ({
      value: cat.id,
      label: cat.name
    }))
  ];

  const filteredVideos = videos.filter(video => {
    const matchesSearch = !searchTerm || 
      video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      video.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || video.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authError = urlParams.get('error');
    const authStatus = urlParams.get('auth');
    const syncMessage = urlParams.get('sync');
    
    if (authStatus === 'error' && authError) {
      const errorMessage = 'YouTube authentication failed: ' + decodeURIComponent(authError);
      toast.error(errorMessage);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (authStatus === 'success') {
      if (syncMessage) {
        toast.success('YouTube authentication successful! ' + decodeURIComponent(syncMessage));
      } else {
        toast.success('YouTube authentication successful!');
      }
      window.history.replaceState({}, document.title, '/admin/instructional-videos');
      refetch();
    }
  }, [refetch]);

  const handleSignIn = async () => {
    if (!canManage) return;
    try {
      const { data } = await getAuthUrlQuery.refetch();
      if (data?.auth_url) {
        const width = 500;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        window.open(
          data.auth_url, 
          '_blank', 
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
      }
    } catch (error) {
      console.error("Error getting auth URL:", error);
      const errorMessage = error?.message || error?.details?.error || "Failed to connect to YouTube.";
      toast.error(errorMessage);
    }
  };

  const handleRefreshData = async () => {
    if (!canManage) return;
    await syncDataMutation.mutateAsync();
  };

  const handleSaveChanges = async (newTitle, newDescription, newCategory) => {
    const title = newTitle.trim();
    const description = newDescription.trim();
    const category = newCategory;

    if (!title || !description || !category) return;

    try {
      await updateVideoMutation.mutateAsync({
        videoId: editingVideo.uuid,
        title,
        description,
        category
      });
      
      setShowEditModal(false);
      setEditingVideo(null);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const videosByCategory = filteredVideos.reduce((acc, video) => {
    const categoryName = video.category_name || 'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(video);
    return acc;
  }, {});

  // const handleVideoSelect = (videoId) => {
  //   setSelectedVideos(prev =>
  //     prev.includes(videoId)
  //       ? prev.filter(id => id !== videoId)
  //       : [...prev, videoId]
  //   );
  // };

  const handleUploadVideo = () => {
    setShowUploadModal(true);
  };

  const handleAddCategory = () => {
    if (!canManage) return;
    setShowAddCategoryModal(true);
  };

  const handleAddNewCategory = async (name) => {
    try {
      if (!canManage) return;
      await createCategoryMutation.mutateAsync({ name });
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setShowEditModal(true);
  };

  const handleDeleteVideo = async (videoId) => {
    if (!canManage) return;
    const video = videos.find(v => v.uuid === videoId);
    if (!video) return;

    const confirmDelete = window.confirm("Are you sure you want to delete this video?");
    if (!confirmDelete) return;

    try {
      await deleteVideoMutation.mutateAsync({ videoId });
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const handleBackButton = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className={styles.spinner_container}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.spinner_container}>
        <div>
          <p>Error loading data. Please try again.</p>
          <Button onClick={refetch}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.dashboard}>
        {syncDataMutation.isPending && (
          <div className={styles.syncOverlay} role="status" aria-live="polite">
            <div className={styles.syncOverlayCard}>
              <Spinner scale={4} color="#0F1A52" />
              <div className={styles.syncOverlayText}>Syncing YouTube videos…</div>
            </div>
          </div>
        )}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.leftHeader}>
              <button className={styles.backButton} onClick={handleBackButton}>
                <FaArrowLeft className={styles.backIcon} />
              </button>
              <div className={styles.titleContainer}>
                <h1 className={styles.title}>Instructional Videos</h1>
                <p className={styles.subtitle}>Manage and share educational content</p>
              </div>
            </div>
            <div className={styles.actionButtons}>
              {canManage && (
                <>
                  {isSignedIn ? (
                    <>
                      <Button className={styles.uploadButton} onClick={handleRefreshData}>
                        Refresh Data
                      </Button>
                      <Button className={styles.addCategoryButton} onClick={() => setShowAddCategoryModal(true)}>
                        + Add Category
                      </Button>
                      <Button className={styles.uploadButton} onClick={() => setShowUploadModal(true)}>
                        <FaUpload className={styles.buttonIcon} />
                        Upload Video
                      </Button>
                    </>
                  ) : (
                    <Button className={styles.uploadButton} onClick={handleSignIn}>
                      Sign in with Google
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className={styles.summaryCards}>
          <Card className={styles.summaryCard}>
            <CardContent className={styles.summaryCardContent}>
              <div className={styles.summaryInfo}>
                <div className={styles.summaryNumber}>{videos.length}</div>
                <div className={styles.summaryLabel}>Total Videos</div>
              </div>
            </CardContent>
          </Card>

          <Card className={styles.summaryCard}>
            <CardContent className={styles.summaryCardContent}>
              <div className={styles.summaryInfo}>
                <div className={styles.summaryNumber}>{categories.length - 1}</div>
                <div className={styles.summaryLabel}>Categories</div>
              </div>
            </CardContent>
          </Card>

          {/* <Card className={styles.summaryCard}>
            <CardContent className={styles.summaryCardContent}>
              <div className={styles.summaryInfo}>
                <div className={styles.summaryNumber}>{selectedVideos.length}</div>
                <div className={styles.summaryLabel}>Selected Videos</div>
              </div>
            </CardContent>
          </Card> */}

          <Card className={styles.summaryCard}>
            <CardContent className={styles.summaryCardContent}>
              <div className={styles.summaryInfo}>
                <div className={styles.summaryNumber}>{categories.length - 1}</div>
                <div className={styles.summaryLabel}>Active Categories</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className={styles.controls}>
          <div className={styles.searchContainer}>
            <CustomInput
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
              startIcon={<FaMagnifyingGlass />}
            />
          </div>

          <div className={styles.filterContainer}>
            <Dropdown
              options={categories}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="All Categories"
              trigger={
                <div className={styles.dropdownTrigger} style={{ padding: '0.75rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <FiFilter style={{ fontSize: '0.875rem' }} />
                  <span style={{ flex: 1 }}>
                    {categories.find(cat => cat.value === selectedCategory)?.label || "All Categories"}
                  </span>
                  <FaAngleDown style={{ fontSize: '0.875rem' }} />
                </div>
              }
            />
          </div>
        </div>

        <div className={styles.videoCategories}>
          {Object.entries(videosByCategory).map(([category, categoryVideos]) => (
            <div key={category} className={styles.categorySection}>
              <div className={styles.categoryHeader}>
                <div className={styles.categoryLeft}>
                  <div className={styles.categoryIcon}></div>
                  <h2 className={styles.categoryTitle}>{category}</h2>
                </div>
                <span className={styles.countBadge}>
                  {categoryVideos.length} {categoryVideos.length === 1 ? "video" : "videos"}
                </span>
              </div>
              <div className={styles.videoGrid}>
                {categoryVideos.map(video => (
                  <Card key={video.uuid} className={styles.videoCard}>
                    <div className={styles.videoThumbnail} onClick={() => {
                      setCurrentVideo(video);
                      setShowPlayModal(true);
                    }}>
                      <IoVideocamOutline className={styles.cameraIcon} />
                      {video.youtube_video_id && (
                        <img 
                          src={`https://img.youtube.com/vi/${video.youtube_video_id}/hqdefault.jpg`} 
                          alt={video.title}
                          className={styles.thumbnailImage}
                        />
                      )}
                      <div className={styles.duration}>{parseDuration(video.duration)}</div>
                      <div className={styles.playOverlay}>
                        <FaPlay className={styles.playIcon} />
                      </div>
                    </div>
                    <CardContent className={styles.videoContent}>
                      <div className={styles.videoHeader}>
                        <div className={styles.videoInfo}>
                          <h3 className={styles.videoTitle}>{video.title}</h3>
                          <p className={styles.videoDescription}>{video.description}</p>
                        </div>
                        <div className={styles.videoActionsHeader}>
                          {canManage && isSignedIn && (
                            <>
                              <LuPen
                                className={styles.editIcon}
                                onClick={() => handleEditVideo(video)}
                              />
                              <RiDeleteBin6Line
                                className={styles.deleteIcon}
                                onClick={() => handleDeleteVideo(video.uuid)}
                              />
                            </>
                          )}
                        </div>
                      </div>
                      <div className={styles.videoActions}>
                        <p className={styles.videoDate}>{formatDate(video.created_at)}</p>
                        {/* <Badge 
                          className={`${styles.selectBadge} ${selectedVideos.includes(video.uuid) ? styles.selected : ""}`}
                          onClick={() => handleVideoSelect(video.uuid)}
                          dot={false}
                        >
                          {selectedVideos.includes(video.uuid) ? "Selected" : "Select"}
                        </Badge> */}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredVideos.length === 0 && (
          <div className={styles.noResults}>
            <FaVideo className={styles.noResultsIcon} />
            <p>No videos found matching your criteria.</p>
          </div>
        )}
      </div>
      {/* <div className={styles.selectionFooter}>
        <p className={styles.selectionText}>Select videos to send to patients or clients</p>
        <Button 
          disabled={selectedVideos.length === 0}
          onClick={() => selectedVideos.length > 0 && setShowSendModal(true)}
          className={styles.sendVideosButton}
        >
          <FiSend className={styles.sendIcon} /> Send Videos
        </Button>
      </div> */}

      {showUploadModal && (
        <UploadModal
          open={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          categories={categories}
          onAddVideo={handleUploadVideo}
          isSignedIn={isSignedIn}
          requestSignIn={handleSignIn}
          onYouTubeChanged={handleRefreshData}          
        />
      )} 

      {/* {showSendModal && (
        <SendModal
          open={showSendModal}
          onClose={() => setShowSendModal(false)}
          selectedVideos={selectedVideos}
          allVideos={videos}
          onToggleSelect={handleVideoSelect}
        />
      )} */}

      {showAddCategoryModal && (
        <AddCategoryModal
          open={showAddCategoryModal}
          onClose={() => setShowAddCategoryModal(false)}
          onAddCategory={handleAddNewCategory}
        />
      )}

      {showEditModal && (
        <EditVideoModal
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingVideo(null);
          }}
          video={editingVideo}
          categories={categories}
          onSave={handleSaveChanges}
        />
      )}

      {showPlayModal && (
        <PlayVideoModal
          open={showPlayModal}
          onClose={() => setShowPlayModal(false)}
          video={currentVideo}
        />
      )}
    </>
  );
};

export default InstructionalVideoDashboard;